import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: number;
}

// Right-hand panel on desktop, bottom sheet on mobile (per the finance mockups).
const SideDrawer = ({
  open,
  onClose,
  title,
  children,
  width = 440,
}: SideDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": isMobile
          ? {
              width: "100%",
              maxHeight: "92vh",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }
          : { width: `${width}px`, maxWidth: "100%", height: "100vh" },
      }}
    >
      <Box p={{ xs: "16px 20px 24px", sm: "24px" }} sx={{ overflowY: "auto" }}>
        {isMobile && (
          <Box
            width="40px"
            height="4px"
            bgcolor="#E4E7EC"
            borderRadius="2px"
            mx="auto"
            mb="16px"
          />
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb="12px"
          mb="16px"
          borderBottom={isMobile ? "1px solid #E4E7EC" : "none"}
        >
          <Typography fontSize={{ xs: 20, sm: 24 }} fontWeight={600} color="#101828">
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ bgcolor: "#F2F4F7", "&:hover": { bgcolor: "#E4E7EC" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {children}
      </Box>
    </Drawer>
  );
};

export default SideDrawer;

export const fieldLabelSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#101828",
  mb: "6px",
};

export const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#FFFFFF" },
};

export const primaryButtonSx = {
  bgcolor: "#0b1c12",
  height: "48px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": { bgcolor: "#08150e" },
  "&.Mui-disabled": { bgcolor: "#E4E7EC", color: "#98A2B3" },
};

export const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={fieldLabelSx}>{children}</Typography>
);

export const SummaryRow = ({
  label,
  value,
  bold,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) => (
  <Box display="flex" justifyContent="space-between" gap="12px" py="8px">
    <Typography fontSize={14} color="#667085">
      {label}
    </Typography>
    <Typography
      fontSize={14}
      fontWeight={bold ? 700 : 500}
      color="#101828"
      textAlign="right"
    >
      {value}
    </Typography>
  </Box>
);

export const todayISODate = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Converts a YYYY-MM-DD input value into an ISO date-time that is never in
// the future (the API rejects future `date` values).
export const dateInputToISO = (value: string) => {
  if (!value) return new Date().toISOString();
  if (value === todayISODate()) return new Date().toISOString();
  return new Date(`${value}T12:00:00`).toISOString();
};
